import { useRef, useEffect } from "react";
import { useEnv } from "store/env";
import { DISMISS, useNotification } from "collections";
import { useTranslation } from "utilities/i18n";
import {
  UserAgentApplication,
  AuthenticationParameters,
  AuthResponse
} from "msal";

export const useMicrosoftAuth = () => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const { microsoftClientId } = useEnv();
  const userAgent = useRef(null as UserAgentApplication | null);

  useEffect(() => {
    // Instantiate MSAL user agent on mount.
    userAgent.current = new UserAgentApplication({
      auth: {
        clientId: microsoftClientId,
        redirectUri: window.location.origin
      }
    });
    // Clear MSAL user agent reference on unmount.
    return () => {
      userAgent.current = null;
    };
  }, [microsoftClientId]);

  const authParameters: AuthenticationParameters = { scopes: [".default"] };

  return {
    getAccessToken: async (): Promise<AuthResponse | undefined> => {
      if (!userAgent.current) return;
      // Attempt to get token silently (only works if already logged in)
      try {
        return await userAgent.current.acquireTokenSilent(authParameters);
      } catch (error) {
        // Fall back to full login and token aquisition flow
        try {
          await userAgent.current.loginPopup(authParameters);
          return await userAgent.current.acquireTokenPopup(authParameters);
        } catch (error) {
          console.error(error);
          addNotification({
            type: "error",
            id: "microsoft-auth-error",
            message: `${t("AUTH_ERROR")}: ${error.message}`,
            dismiss: DISMISS.USER_ONLY,
            persistThroughNav: true
          });
        }
      }
    }
  };
};
